import styled from '@emotion/styled'
import {
  borderRadius,
  Button,
  colors,
  fontSize,
  fontWeight,
  spacing,
  Text,
  PageLayout,
  PageLayoutContent,
} from '@retailer-platform/shared-components'
import React, { type FunctionComponent, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { ProductsViewImage } from 'domains/products/components/view/components/image/ProductsViewImage'
import { useProductImages } from 'domains/products/components/view/utils/useProductImages.hooks'
import FilterBar from 'legacy/components/FilterBar'
import { useProductAttributes } from 'domains/products/components/view/utils/useProductAttributes.hooks'
import { useProductCategories } from 'domains/products/components/view/utils/useProductCategories.hooks'
import {
  RetailerLocaleSelect,
  type OnLocaleChangeCallback,
} from 'gin-and-tonic/containers/locale-select/LocaleSelect'
import { trackEvent } from 'utils/events/trackEvent'
import { useDomainGoToPath } from 'domains/products/utils/domain/routing'
import { type DomainRouteName } from 'domains/products/routing/routes'
import { productsViewVBOAccessControl } from 'domains/products/access-control/viewAccess.configuration'
import { productsViewInternalAccessControl } from 'domains/products/access-control/viewAccess.configuration'
import { OverrideIcon } from 'domains/products/components/view/components/details/components/OverrideIcon'
import { useGetValueBasedOverrides } from 'domains/products/api/getValueBasedOverrides.hooks'
import {
  EditAttributes,
  useAttributeEditAttributes,
} from 'domains/products/components/view/utils/useAttributeEditAttributes.hooks'
import { type Product } from 'domains/products/components/view/utils/types'
import { AttributeOverride } from 'domains/products/components/view/components/attribute-override/AttributeOverride'
import { AttributeOverrideSuccess } from 'domains/products/components/view/components/attribute-override-success/AttributeOverrideSuccess'
import { useDomainAccessControl } from 'domains/products/utils/domain/accessControl'

interface ProductsViewDetailsProps {
  product: Product
  retailerId: string
  showLocaleSelector?: boolean
  locale: string
  onLocaleChange: OnLocaleChangeCallback
}

const ContainerSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  background: colors.GRAYSCALE.X0,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colors.GRAYSCALE.X20,
  borderRadius: borderRadius.X4,
  width: '720px',
  margin: spacing.X24 + ' auto ' + spacing.X24 + ' auto ',
})

const Row = styled.div({
  display: 'flex',
  flexDirection: 'row',
})

const AttributesContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: '24px',
})

const Layout = styled.div<{ numColumns: number }>(
  {
    display: 'flex',
    flexDirection: 'row',
    gap: spacing.X20,
    maxWidth: 1080,
  },
  ({ numColumns }) => ({
    '& > div': {
      flexBasis: `${100 / numColumns}%`,
    },
  })
)

const ImagesLayout = styled.div({
  display: 'flex',
  gap: spacing.X20,
  marginTop: spacing.X20,
  width: '100%',
  flexWrap: 'wrap',
})

const Column = styled.div({})

const Section = styled.section({
  h3: {
    fontWeight: fontWeight.SEMIBOLD,
    fontSize: fontSize.X15,
    color: colors.GRAYSCALE.X70,
    marginBottom: spacing.X4,
  },

  fontSize: fontSize.X12,
  color: colors.GRAYSCALE.X50,
  marginBottom: spacing.X32,
})

const SectionHeader = styled.div({
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: colors.GRAYSCALE.X20,
  borderBottomRadius: borderRadius.X4,
  padding: '16px 24px',
})

const SectionHeaderTitle = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const Title = styled.div({
  fontSize: fontSize.X18,
  fontWeight: fontWeight.BOLD,
})

const LocaleSelectorContainer = styled.div({
  paddingTop: spacing.X16,
  paddingBottom: spacing.X16,
  width: 140,
  alignItems: 'center',
})

const unavailable = '–'

export const testIds = {
  productInfo: 'product-details-productInfo',
  images: 'product-details-images',
  productName: 'product-details-productName',
  brandName: 'product-details-brandName',
  pricedBy: 'product-details-pricedBy',
  description: 'product-details-description',
  retailerReferenceCode: 'product-details-retailerReferenceCode',
  productCodes: 'product-details-productCodes',
  unit: 'product-details-unit',
  parWeightAdjusted: 'product-details-parWeightAdjusted',
  ingredients: 'product-details-ingredients',
  category: 'product-details-category',
  size: 'product-details-size',
  attributes: 'product-details-attributes',
  primaryImages: 'product-details-primaryImages',
  secondaryImages: 'product-details-secondaryImages',
}

export const ProductsViewDetails: FunctionComponent<ProductsViewDetailsProps> = ({
  product,
  retailerId,
  showLocaleSelector,
  locale,
  onLocaleChange,
}) => {
  const messages = useDomainMessages({
    productInfo: 'productsDomain.view.details.titles.productInfo',
    productInfoDescription: 'productsDomain.view.details.titles.productInfoDescription',
    images: 'productsDomain.view.details.titles.images',
    productName: 'productsDomain.view.details.sections.productName',
    brandName: 'productsDomain.view.details.sections.brandName',
    pricedBy: 'productsDomain.view.details.sections.pricedBy',
    description: 'productsDomain.view.details.sections.description',
    retailerReferenceCode: 'productsDomain.view.details.sections.retailerReferenceCode',
    productCodes: 'productsDomain.view.details.sections.productCodes',
    unit: 'productsDomain.view.details.sections.unit',
    parWeightAdjusted: 'productsDomain.view.details.sections.parWeightAdjusted',
    ingredients: 'productsDomain.view.details.sections.ingredients',
    category: 'productsDomain.view.details.sections.category',
    size: 'productsDomain.view.details.sections.size',
    attributes: 'productsDomain.view.details.sections.attributes',
    primaryImages: 'productsDomain.view.details.sections.images.primaryImages',
    secondaryImages: 'productsDomain.view.details.sections.images.secondaryImages',
  })

  enum AttributeEditState {
    Default,
    Edit,
    Success,
  }
  const hasAccess = useDomainAccessControl()
  const isInternal = hasAccess(productsViewInternalAccessControl)

  const [attributeEditState, setAttributeEditState] = useState<AttributeEditState>(
    AttributeEditState.Default
  )

  const attributes = useProductAttributes(product)
  const categories = useProductCategories(product.productCategoryTaxonomy)
  const secondaryImages = useProductImages(product.imageList)
  const productId = product.productId

  const { attributes: overrideAttributes } = useAttributeEditAttributes()
  const { valueBasedOverrides } = useGetValueBasedOverrides(retailerId, productId)

  const goToValueOverrides = useDomainGoToPath('products-view-attribute-overrides')
  const goToProductEdit = useDomainGoToPath('editProduct' as DomainRouteName, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    product_id: productId,
  })
  const goToEditImages = useDomainGoToPath('products-edit-images')

  const canAccessVBO = hasAccess(productsViewVBOAccessControl)

  const onEditProduct = () => {
    if (canAccessVBO) {
      setAttributeEditState(AttributeEditState.Edit)
    } else {
      goToProductEdit()
    }
  }

  const onAttributeEditClose = () => {
    setAttributeEditState(AttributeEditState.Default)
  }

  const onAttributeEditSuccess = () => {
    setAttributeEditState(AttributeEditState.Success)
  }

  useEffect(() => {
    trackEvent({
      id: 'products.product_details.viewed',
      description: 'Viewed the product details page for a specific product',
      data: { locale, productId },
    })
  }, [locale, productId])

  const overrideDictionary = useMemo(
    () =>
      (valueBasedOverrides ?? []).reduce((previousValue, currentValue) => {
        for (const enumValue in EditAttributes) {
          const editAttribute = overrideAttributes[enumValue]
          if (!editAttribute) return previousValue
          const displayName = editAttribute?.displayName
          if (currentValue?.attributeName === displayName) {
            return {
              ...previousValue,
              [editAttribute.name]: currentValue,
            }
          }
        }
        return previousValue
      }, {}),
    [overrideAttributes, valueBasedOverrides]
  )

  return (
    <PageLayout noSidebar noHeader noFooter>
      <PageLayoutContent css={{ backgroundColor: colors.GRAYSCALE.X10 }}>
        <ContainerSection>
          <SectionHeader>
            <SectionHeaderTitle>
              <Title>{messages.productInfo}</Title>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {showLocaleSelector && (
                  <div>
                    <LocaleSelectorContainer>
                      <RetailerLocaleSelect onChange={onLocaleChange} initialValue={locale} />
                    </LocaleSelectorContainer>
                  </div>
                )}
                {attributeEditState !== AttributeEditState.Edit && (
                  <div>
                    <Button onClick={onEditProduct} variant={'secondary'}>
                      <FormattedMessage id="productsDomain.view.header.editProduct" />
                    </Button>
                  </div>
                )}
              </div>
            </SectionHeaderTitle>
            <div>
              <Text>{messages.productInfoDescription}</Text>
            </div>
          </SectionHeader>
          <AttributesContainer>
            <Layout numColumns={3}>
              <Column>
                <Section>
                  <Row>
                    <h3>{messages.productName}</h3>
                    <OverrideIcon
                      onClick={goToValueOverrides}
                      valueBasedOverride={overrideDictionary[EditAttributes.originalName]}
                    />
                  </Row>
                  <p data-testid={testIds.productName}>{product.originalName ?? unavailable}</p>
                </Section>

                <Section>
                  <Row>
                    <h3>{messages.brandName}</h3>
                    <OverrideIcon
                      onClick={goToValueOverrides}
                      valueBasedOverride={overrideDictionary[EditAttributes.brandName]}
                    />
                  </Row>
                  <p data-testid={testIds.brandName}>{product.brandName ?? unavailable}</p>
                </Section>

                <Section>
                  <h3>{messages.pricedBy}</h3>
                  <p data-testid={testIds.pricedBy}>{unavailable}</p>
                </Section>

                <Section>
                  <Row>
                    <h3>{messages.description}</h3>
                    <OverrideIcon
                      onClick={goToValueOverrides}
                      valueBasedOverride={overrideDictionary[EditAttributes.details]}
                    />
                  </Row>
                  <p data-testid={testIds.description}>{product.details ?? unavailable}</p>
                </Section>

                <Section>
                  <h3>{messages.retailerReferenceCode}</h3>
                  <p data-testid={testIds.retailerReferenceCode}>
                    {product.retailerReferenceCode ?? unavailable}
                  </p>
                </Section>
              </Column>

              <Column>
                <Section>
                  <h3>{messages.productCodes}</h3>
                  <p data-testid={testIds.productCodes}>
                    {product?.productCodes?.length > 0
                      ? product.productCodes.join(', ')
                      : unavailable}
                  </p>
                </Section>

                <Section>
                  <h3>{messages.unit}</h3>
                  <p data-testid={testIds.unit}>{product.unitCount ?? unavailable}</p>
                </Section>

                <Section>
                  <h3>{messages.parWeightAdjusted}</h3>
                  <p data-testid={testIds.parWeightAdjusted}>{unavailable}</p>
                </Section>

                <Section>
                  <h3>{messages.ingredients}</h3>
                  <p data-testid={testIds.ingredients}>{product.ingredients ?? unavailable}</p>
                </Section>
              </Column>

              <Column>
                <Section>
                  <h3>{messages.category}</h3>
                  <p data-testid={testIds.category}>{categories ?? unavailable}</p>
                </Section>

                <Section>
                  <h3>{messages.size}</h3>
                  <p data-testid={testIds.size}>{product.size ?? unavailable}</p>
                </Section>

                <Section>
                  <h3>{messages.attributes}</h3>
                  <p data-testid={testIds.attributes}>{attributes ?? unavailable}</p>
                </Section>
              </Column>
            </Layout>
          </AttributesContainer>
        </ContainerSection>
        <ContainerSection>
          <SectionHeader>
            <SectionHeaderTitle>
              <Title>{messages.images}</Title>
              <Button onClick={goToEditImages} variant={'secondary'}>
                <FormattedMessage id="productsDomain.view.header.editImages" />
              </Button>
            </SectionHeaderTitle>
          </SectionHeader>
          <AttributesContainer>
            <Section>
              <h3>{messages.primaryImages}</h3>

              <ImagesLayout data-testid={testIds.primaryImages}>
                <ProductsViewImage url={product.primaryImageUrl} />
              </ImagesLayout>
            </Section>
            <Section>
              <h3>{messages.secondaryImages}</h3>

              <ImagesLayout data-testid={testIds.secondaryImages}>
                {secondaryImages.map(image => (
                  <ProductsViewImage url={image.url} key={image.type} label={image.label} />
                ))}
              </ImagesLayout>
            </Section>
          </AttributesContainer>
        </ContainerSection>
        {attributeEditState === AttributeEditState.Edit && (
          <AttributeOverride
            product={product}
            retailerId={retailerId}
            onClose={onAttributeEditClose}
            onSuccess={onAttributeEditSuccess}
            showRetailerForm={!isInternal}
          />
        )}
        {attributeEditState === AttributeEditState.Success && <AttributeOverrideSuccess />}
      </PageLayoutContent>
    </PageLayout>
  )
}
